import React, { useState, useEffect, useCallback } from 'react'
import {
    Button,
    Grid,
    makeStyles,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Typography,
    Chip,
} from '@material-ui/core'
import {
    MailOutline as OnboardIcon,
    ExitToApp as OffboardIcon,
    Assignment as SurveyIcon,
    NotificationsOutlined as ReminderIcon,
    HelpOutline as OtherIcon,
    ChevronRight as ArrowIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
} from '@material-ui/icons'

import { BackdropProgress } from '../../components'
import { useAuthentication } from 'components'
import { send_command } from 'components/api'
import { decodeEmailTemplateBody } from 'utils/decodeEmailTemplateBody'

const TYPE_ICONS = {
    onboarding:  { icon: OnboardIcon,  bg: '#e0f5ef' },
    offboarding: { icon: OffboardIcon, bg: '#ffe8e8' },
    survey:      { icon: SurveyIcon,   bg: '#fff3dc' },
    reminder:    { icon: ReminderIcon, bg: '#ede8ff' },
}

function getTypeConfig(email_type) {
    const key = (email_type || '').toLowerCase()
    for (const [k, v] of Object.entries(TYPE_ICONS)) {
        if (key.includes(k)) return v
    }
    return { icon: OtherIcon, bg: '#eef0f4' }
}

function renderEmailBody(body) {
    try {
        return body.replaceAll(
            '<%= it.tenant_img %>',
            'https://s3.us-west-2.amazonaws.com/cdn.dataprotected.com/Logo.png'
        )
    } catch {
        return body
    }
}

const useStyles = makeStyles(theme => ({
    filters: {
        display: 'flex',
        gap: 12,
        marginBottom: 16,
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    filterSelect: {
        minWidth: 160,
        backgroundColor: '#fff',
    },
    emailList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 16,
    },
    emailRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        border: '1px solid #d8dee6',
        borderRadius: 7,
        cursor: 'pointer',
        transition: 'background .12s, border-color .12s',
        backgroundColor: '#fff',
        '&:hover': {
            backgroundColor: '#f0f9fb',
            borderColor: '#00b4c8',
        },
    },
    emailRowSelected: {
        backgroundColor: '#e5f6f9',
        borderColor: '#00b4c8',
    },
    typeIcon: {
        width: 34,
        height: 34,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    emailInfo: {
        flex: 1,
        minWidth: 0,
    },
    emailTitle: {
        fontWeight: 600,
        fontSize: '0.9rem',
        marginBottom: 2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    emailMeta: {
        fontSize: '0.78rem',
        color: '#6b7585',
    },
    langChip: {
        fontSize: '0.68rem',
        fontWeight: 700,
        height: 20,
    },
    preview: {
        border: '1px solid #d8dee6',
        borderRadius: 8,
        overflow: 'hidden',
        marginTop: 8,
    },
    previewHeader: {
        backgroundColor: '#f4f6f8',
        padding: '10px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #d8dee6',
    },
    previewLabel: {
        fontSize: '0.82rem',
        color: '#6b7585',
    },
    previewBody: {
        padding: 24,
        fontSize: '0.88rem',
        lineHeight: 1.6,
        backgroundColor: '#fff',
        maxHeight: 360,
        overflowY: 'auto',
    },
    empty: {
        textAlign: 'center',
        padding: '32px 0',
        color: '#6b7585',
    },
    refreshBtn: {
        marginLeft: 'auto',
    },
}))

export default function ManageEmails() {
    const classes = useStyles()
    const user = useAuthentication()
    const tenantId = user?.attributes?.['custom:tenantid']

    const [isLoading, setIsLoading] = useState(false)
    const [emailData, setEmailData] = useState(null)
    const [fetchError, setFetchError] = useState(null)
    const [filterType, setFilterType] = useState('')
    const [filterLang, setFilterLang] = useState('')
    const [selectedEmail, setSelectedEmail] = useState(null)

    const fetchEmails = useCallback(() => {
        if (!tenantId) return
        setIsLoading(true)
        setFetchError(null)
        send_command('ADMIN_GET_TENANT_EMAILS', { tenant_id: tenantId })
            .then(data => {
                setEmailData(Array.isArray(data) ? data : [])
            })
            .catch(() => {
                setFetchError('Failed to load email templates.')
            })
            .finally(() => setIsLoading(false))
    }, [tenantId])

    useEffect(() => {
        fetchEmails()
    }, [fetchEmails])

    if (isLoading) {
        return <BackdropProgress />
    }

    if (fetchError) {
        return (
            <Grid container>
                <Grid item xs={12}>
                    <Typography color="error">{fetchError}</Typography>
                    <Button size="small" variant="outlined" onClick={fetchEmails} style={{ marginTop: 8 }}>Retry</Button>
                </Grid>
            </Grid>
        )
    }

    const emails = Array.isArray(emailData) ? emailData : []

    const emailTypes = [...new Set(emails.map(e => e.email_type).filter(Boolean))].sort()
    const emailLangs = [...new Set(emails.map(e => e.language).filter(Boolean))].sort()

    const filtered = emails.filter(e => {
        const typeMatch = !filterType || e.email_type === filterType
        const langMatch = !filterLang || e.language === filterLang
        return typeMatch && langMatch
    })

    const handleSelectEmail = (email) => {
        setSelectedEmail(prev => (prev && prev.email_type === email.email_type && prev.language === email.language)
            ? null
            : email
        )
    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <div className={classes.filters}>
                    <FormControl variant="outlined" size="small" className={classes.filterSelect}>
                        <InputLabel>All Types</InputLabel>
                        <Select
                            value={filterType}
                            onChange={e => { setFilterType(e.target.value); setSelectedEmail(null) }}
                            label="All Types"
                        >
                            <MenuItem value="">All Types</MenuItem>
                            {emailTypes.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" className={classes.filterSelect}>
                        <InputLabel>All Languages</InputLabel>
                        <Select
                            value={filterLang}
                            onChange={e => { setFilterLang(e.target.value); setSelectedEmail(null) }}
                            label="All Languages"
                        >
                            <MenuItem value="">All Languages</MenuItem>
                            {emailLangs.map(l => (
                                <MenuItem key={l} value={l}>{l}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        className={classes.refreshBtn}
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={() => { fetchEmails(); setSelectedEmail(null) }}
                    >
                        Refresh
                    </Button>
                </div>
            </Grid>

            <Grid item xs={12}>
                {filtered.length === 0 ? (
                    <Typography className={classes.empty}>
                        {emails.length === 0
                            ? 'No email templates found.'
                            : 'No templates match the selected filters.'}
                    </Typography>
                ) : (
                    <div className={classes.emailList}>
                        {filtered.map(email => {
                            const cfg = getTypeConfig(email.email_type)
                            const Icon = cfg.icon
                            const isSelected = selectedEmail
                                && selectedEmail.email_type === email.email_type
                                && selectedEmail.language === email.language
                            return (
                                <div
                                    key={`${email.email_type}-${email.language}`}
                                    className={`${classes.emailRow} ${isSelected ? classes.emailRowSelected : ''}`}
                                    onClick={() => handleSelectEmail(email)}
                                >
                                    <div className={classes.typeIcon} style={{ backgroundColor: cfg.bg }}>
                                        <Icon fontSize="small" style={{ color: '#555' }} />
                                    </div>
                                    <div className={classes.emailInfo}>
                                        <div className={classes.emailTitle}>
                                            {email.subject || email.email_type}
                                        </div>
                                        <div className={classes.emailMeta}>
                                            {email.email_type}
                                        </div>
                                    </div>
                                    <Chip
                                        label={email.language}
                                        size="small"
                                        className={classes.langChip}
                                        variant="outlined"
                                    />
                                    <ArrowIcon
                                        fontSize="small"
                                        style={{
                                            color: '#00b4c8',
                                            transform: isSelected ? 'rotate(90deg)' : 'none',
                                            transition: 'transform .15s',
                                        }}
                                    />
                                </div>
                            )
                        })}
                    </div>
                )}
            </Grid>

            {selectedEmail && (
                <Grid item xs={12}>
                    <div className={classes.preview}>
                        <div className={classes.previewHeader}>
                            <span className={classes.previewLabel}>
                                {selectedEmail.email_type} — {selectedEmail.language}
                                {selectedEmail.subject ? ` · ${selectedEmail.subject}` : ''}
                            </span>
                            <Button
                                size="small"
                                startIcon={<CloseIcon />}
                                onClick={() => setSelectedEmail(null)}
                            >
                                Close
                            </Button>
                        </div>
                        <div
                            className={classes.previewBody}
                            dangerouslySetInnerHTML={{
                                __html: renderEmailBody(
                                    decodeEmailTemplateBody(selectedEmail.body || '')
                                ) || '<p style="color:#9aa3b2">No preview available for this template.</p>'
                            }}
                        />
                    </div>
                </Grid>
            )}
        </Grid>
    )
}
